import React, { useEffect, useState, useSyncExternalStore } from 'react';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Toll as TokenIcon,
  Timer as TimerIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../theme/ThemeProvider';
import HuttonsWhite from '../../img/Huttons_white.png';
import HuttonsBlue from '../../img/Huttons_blue.png';
import {
  getShareTokens,
  subscribeShareTokens,
  getShareExpiry,
  subscribeShareExpiry,
  getInvitingAgent,
  subscribeInvitingAgent,
  type ShareTokens,
  type ShareInvitingAgent,
} from '../services/shareContext';

/**
 * Re-renders the host component whenever the share-context token counters
 * change. Returns `null` for non-share (agent EXTERNAL) sessions where no
 * counters have been installed.
 */
const useShareTokens = (): ShareTokens | null =>
  useSyncExternalStore(subscribeShareTokens, getShareTokens, () => null);

/** Same pattern as `useShareTokens`, for the inviting agent. */
const useInvitingAgent = (): ShareInvitingAgent | null =>
  useSyncExternalStore(subscribeInvitingAgent, getInvitingAgent, () => null);

/** Live ms-since-epoch deadline; `null` when no expiry has been set. */
const useShareExpiry = (): number | null =>
  useSyncExternalStore(subscribeShareExpiry, getShareExpiry, () => null);

/**
 * Human-readable remaining trial time. Long periods show days/hours; the
 * final hour uses an MM:SS countdown so the last minutes stay scannable.
 */
const formatRemaining = (ms: number): string => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  if (days >= 1) {
    const dayLabel = `${days} day${days === 1 ? '' : 's'}`;
    if (hours > 0) return `${dayLabel}, ${hours} hr`;
    return dayLabel;
  }
  if (hours >= 1) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

const formatTrialEndTooltip = (expiresAt: number): string => {
  const formatted = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(expiresAt));
  return `Your trial ends on ${formatted}`;
};

/**
 * Header chip that ticks once a second toward the share's expiry
 * timestamp. Hidden in agent EXTERNAL mode (no expiry installed) and
 * for legacy rows that never had a `CreatedAt` (the resolve route
 * returns `null` and the SPA never expires them).
 */
const TrialCountdown: React.FC = () => {
  const expiresAt = useShareExpiry();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (expiresAt == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  if (expiresAt == null) return null;
  const remainingMs = expiresAt - now;
  // Red styling in the final hour; auto-kick still fires at zero.
  const isUrgent = remainingMs > 0 && remainingMs <= 60 * 60 * 1000;
  const isExpired = remainingMs <= 0;
  return (
    <Tooltip
      title={
        isExpired
          ? 'Trial period has ended.'
          : formatTrialEndTooltip(expiresAt)
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          bgcolor: isUrgent || isExpired
            ? 'rgba(244, 67, 54, 0.12)'
            : 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
          border: `1px solid ${
            isUrgent || isExpired
              ? 'rgba(244, 67, 54, 0.4)'
              : 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.3)'
          }`,
        }}
      >
        <TimerIcon
          sx={{
            fontSize: '1.05rem',
            color: isUrgent || isExpired ? '#f44336' : 'var(--color-accent)',
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: 'var(--color-text-strong)',
            display: { xs: 'none', sm: 'inline' },
          }}
        >
          Trial ends in:
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.95rem',
            fontWeight: 700,
            color: isUrgent || isExpired ? '#f44336' : 'var(--color-accent)',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {isExpired ? 'Expired' : formatRemaining(remainingMs)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const agentInitials = (name: string | null | undefined, fallback: string): string => {
  const source = (name && name.trim().length > 0 ? name : fallback).trim();
  if (!source) return '?';
  return source
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

export const PublicHeader: React.FC = () => {
  const { currentTheme, setTheme } = useAppTheme();
  const isDark = currentTheme === 'dark';
  const logoSrc = isDark ? HuttonsWhite : HuttonsBlue;
  const tokens = useShareTokens();
  const agent = useInvitingAgent();
  const [agentAnchor, setAgentAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box
      component="header"
      sx={{
        height: 64,
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text-strong)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pl: { xs: 2, md: 4 },
        pr: { xs: 2, md: 5 },
        boxShadow: '0 2px 4px rgba(var(--color-shadow-rgb), 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src={logoSrc}
          alt="Huttons"
          sx={{ height: 28, width: 'auto', display: 'block' }}
        />
        <Typography
          component="span"
          sx={{
            color: 'var(--color-text-strong)',
            fontSize: '0.9rem',
            fontWeight: 400,
            display: { xs: 'none', sm: 'inline' },
            whiteSpace: 'nowrap',
          }}
        >
          | SUPER Picker
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
        <TrialCountdown />
        {tokens && (
          <Tooltip
            title={`You have ${tokens.remaining} of ${tokens.max} tokens left`}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: { xs: 1.25, sm: 1.75 },
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.12)',
                border: '1px solid rgba(var(--color-accent-rgb, 232, 160, 70), 0.4)',
                color: 'var(--color-text-strong)',
              }}
            >
              <TokenIcon
                sx={{
                  color: 'var(--color-accent)',
                  fontSize: { xs: '1.25rem', sm: '1.4rem' },
                }}
              />
              {/* Label hidden on the very smallest screens to keep the
                  header from wrapping, but the count itself is always shown. */}
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 600,
                  lineHeight: 1,
                  display: { xs: 'none', sm: 'inline' },
                  color: 'var(--color-text-strong)',
                }}
              >
                Remaining tokens:
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '1.05rem', sm: '1.15rem' },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'var(--color-accent)',
                }}
              >
                {tokens.remaining}
                <Box
                  component="span"
                  sx={{
                    opacity: 0.65,
                    fontWeight: 500,
                    ml: 0.5,
                    fontSize: '0.85em',
                    display: { xs: 'none', sm: 'inline' },
                  }}
                >
                  {` / ${tokens.max}`}
                </Box>
              </Typography>
            </Box>
          </Tooltip>
        )}
        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            sx={{
              color: 'var(--color-text-strong)',
              '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
            }}
          >
            {isDark ? <LightIcon /> : <DarkIcon />}
          </IconButton>
        </Tooltip>

        {/* Agent profile button — only rendered when the share resolved to a
            known inviting agent. Clicking opens a popover with CEA, phone
            and email. Mirrors the layout of the agent shell's profile menu
            (see `super-picker/components/header/AppHeader.tsx`). */}
        {agent && (
          <>
            <Tooltip title="Your agent">
              <Box
                onClick={(e) => setAgentAnchor(e.currentTarget)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
                  },
                }}
              >
                <Avatar
                  src={agent.photo ?? undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'rgba(var(--color-text-rgb), 0.2)',
                    fontSize: '0.85rem',
                    color: 'var(--color-text-strong)',
                    '& img': { objectFit: 'contain', objectPosition: 'center top' },
                  }}
                >
                  {agentInitials(agent.name, agent.ceaNumber)}
                </Avatar>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: 'var(--color-text-strong)',
                    display: { xs: 'none', sm: 'block' },
                    whiteSpace: 'nowrap',
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {agent.name || agent.ceaNumber}
                </Typography>
              </Box>
            </Tooltip>

            <Popover
              open={Boolean(agentAnchor)}
              anchorEl={agentAnchor}
              onClose={() => setAgentAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  minWidth: 280,
                  maxWidth: 320,
                  mt: 1,
                  p: 2,
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar
                  src={agent.photo ?? undefined}
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.2)',
                    color: 'var(--color-text-strong)',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    '& img': { objectFit: 'contain', objectPosition: 'center top' },
                  }}
                >
                  {agentInitials(agent.name, agent.ceaNumber)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                  >
                    {agent.name || 'Your Huttons Agent'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}
                  >
                    Huttons Asia Pte Ltd
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <AgentDetailRow
                icon={<BadgeIcon fontSize="small" />}
                label="CEA Number"
                value={agent.ceaNumber}
              />
              <AgentDetailRow
                icon={<PhoneIcon fontSize="small" />}
                label="Phone"
                value={agent.mobile}
                href={agent.mobile ? `tel:${agent.mobile}` : undefined}
              />
              <AgentDetailRow
                icon={<EmailIcon fontSize="small" />}
                label="Email"
                value={agent.email}
                href={agent.email ? `mailto:${agent.email}` : undefined}
              />
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};

/**
 * Single labelled row inside the agent-details popover. If `href` is set the
 * value renders as a clickable `tel:` / `mailto:` link so the recipient can
 * tap straight into their phone / mail app.
 */
const AgentDetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | null;
  href?: string;
}> = ({ icon, label, value, href }) => {
  const display = value && value.trim().length > 0 ? value : '—';
  const isLink = Boolean(href && value && value.trim().length > 0);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75 }}>
      <Box
        sx={{
          color: 'var(--color-accent)',
          display: 'inline-flex',
          alignItems: 'center',
          mt: '2px',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(var(--color-text-rgb), 0.6)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        {isLink ? (
          <Typography
            component="a"
            href={href}
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--color-accent)',
              textDecoration: 'none',
              wordBreak: 'break-all',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {display}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--color-text-strong)',
              wordBreak: 'break-all',
            }}
          >
            {display}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PublicHeader;
